import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { RichEditor } from "@/components/admin/RichEditor";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { GalleryEditor } from "@/components/admin/GalleryEditor";

const SECTIONS = [
  ["site", "Site & Logo"], ["hero", "Home · Hero"], ["digital", "Digital Edition (Flipbook)"],
  ["fingertips", "Home · Finger Tips"], ["card1", "Home · Card 1 (Digital)"], ["card2", "Home · Card 2 (Print)"],
  ["card3", "Home · Card 3 (List)"], ["device", "Home · Any device"], ["features", "Home · Features"],
  ["statement", "Home · Statement"], ["members", "Home · Members Carousel"], ["editions", "Home · Editions Carousel"],
  ["about", "About Page"], ["listing", "Listing Options Page"],
  ["cat1", "Listing · Gate Fold"], ["cat2", "Listing · Double Page"], ["cat3", "Listing · Full Page"],
  ["cat4", "Listing · Half Page"], ["cat5", "Listing · Photo Listing"], ["cat6", "Listing · Normal Listing"],
  ["buy", "Buy Page"], ["questionnaire", "Listing Questionnaire"], ["mediakit", "Brochure & Media Kit"], ["contact", "Contact"],
  ["hotelpage", "Hotel Page Template"], ["seo", "SEO · Meta Titles & Descriptions"], ["footer", "Footer"],
];

const HINTS = {
  bullets_lines: "One bullet per line", highlights_lines: "One highlight per line", address_lines: "One line per row",
  menu_lines: "One per line as  Label|/url", bank_lines: "One per line as  Label|Value", hero_subtitle_lines: "One line per row",
  features_lines: "One feature per line", price_lines: "One per line as  Label|Price|Note (note optional). The last one is highlighted.", bg_opacity: "0 = invisible, 1 = fully visible",
  placeholder_html: "Use {hotel} and {city} as placeholders", height: "Iframe height in pixels",
  button_url: "e.g. /contact, /listing-questionnaire or https://…", url: "Link to embed, e.g. a Google Form ?embedded=true link", link_url: "e.g. /contact or https://…",
  panel_opacity: "Dark box behind the hero text: 0 = transparent, 1 = solid",
  panel_color: "Colour of the box behind the hero text (its see-through level is set by Panel opacity)",
  sample_image_width: "Size of the sample image on the Listing Options page",
  hotel_title: "Used for every hotel page. {hotel} = hotel name, {city} = city", hotel_description: "Used for every hotel page. {hotel} = hotel name, {city} = city",
};

const typeOf = (k) =>
  k === "images" ? "gallery" : k.endsWith("_color") ? "color" : k.endsWith("_width") ? "percent" : k.endsWith("_html") ? "rich" : (k.endsWith("_lines") || k === "description" || k.endsWith("_description")) ? "lines" : k === "pdf" ? "pdf"
    : /(image|photo|logo)$/.test(k) ? "image" : k === "bg_opacity" || k === "panel_opacity" ? "opacity" : "text";

const labelOf = (k) => {
  const s = k.replace(/_(html|lines)$/, "").replace(/^f(\d)_/, "Feature $1 ").replace(/^team(\d)_/, "Team $1 ").replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const inp = "w-full border border-stone-300 bg-white rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-crimson/30";

export const Field = ({ section, k, value, onChange }) => {
  const t = typeOf(k);
  const id = `content-${section}-${k}`;
  return (
    <div data-testid={`${id}-field`}>
      <label className="text-sm font-bold block mb-1">{labelOf(k)}</label>
      {HINTS[k] && <p className="text-xs text-stone-500 mb-1.5">{HINTS[k]}</p>}
      {t === "rich" && <RichEditor value={value} onChange={onChange} testid={id} />}
      {t === "lines" && <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={5} className={inp} data-testid={id} />}
      {t === "image" && <ImageUpload value={value} onChange={onChange} testid={id} />}
      {t === "pdf" && <ImageUpload value={value} onChange={onChange} testid={id} accept="application/pdf" pdf />}
      {t === "gallery" && <GalleryEditor value={value} onChange={onChange} testid={id} />}
      {t === "opacity" && (
        <div className="flex items-center gap-3">
          <input type="range" min="0" max="1" step="0.05" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 accent-[#C8102E]" data-testid={id} />
          <span className="text-sm w-12 text-right">{value}</span>
        </div>
      )}
      {t === "percent" && (
        <div className="flex items-center gap-3">
          <input type="range" min="10" max="100" step="5" value={value || 100} onChange={(e) => onChange(e.target.value)} className="flex-1 accent-[#C8102E]" data-testid={id} />
          <span className="text-sm font-bold text-crimson w-12 text-right" data-testid={`${id}-value`}>{value || 100}%</span>
        </div>
      )}
      {t === "color" && (
        <div className="flex items-center gap-3">
          <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#1F1C1B"} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 rounded border border-stone-300 cursor-pointer bg-white p-0.5" data-testid={id} />
          <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="#1F1C1B" className={`${inp} max-w-[140px] font-mono`} data-testid={`${id}-hex`} />
          <div className="flex gap-1.5">
            {["#1F1C1B", "#000000", "#C8102E", "#B08D57", "#FFFFFF"].map((c) => (
              <button key={c} type="button" onClick={() => onChange(c)} title={c} className="w-7 h-7 rounded-full border border-stone-300" style={{ background: c }} data-testid={`${id}-swatch-${c.slice(1)}`} />
            ))}
          </div>
        </div>
      )}
      {t === "text" && <input value={value} onChange={(e) => onChange(e.target.value)} className={inp} data-testid={id} />}
    </div>
  );
};

export default function Content() {
  const { content, reload } = useSite();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("hero");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (content && !data) setData(JSON.parse(JSON.stringify(content)));
  }, [content, data]);

  if (!data) return <div className="text-stone-400">Loading…</div>;

  const set = (k) => (v) => setData((d) => ({ ...d, [active]: { ...d[active], [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data: saved } = await api.put("/admin/content", data);
      setData(saved);
      await reload();
      toast.success("Website content saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-content">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Website Content</h1>
          <p className="text-sm text-stone-500 mt-1">Edit any section, then Save. Changes apply to the live site on Save.</p>
        </div>
        <button onClick={save} disabled={busy} className="sticky top-20 inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white rounded-md px-5 py-2.5 text-sm font-bold disabled:opacity-60 shadow-sm" data-testid="content-save-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save Changes
        </button>
      </div>
      <div className="grid lg:grid-cols-[240px_1fr] gap-6 items-start">
        <aside className="bg-white border border-stone-200 rounded-lg p-2 lg:sticky lg:top-20 max-h-[75vh] overflow-y-auto" data-testid="content-sections-nav">
          {SECTIONS.map(([key, label]) => (
            <button key={key} onClick={() => setActive(key)} className={`w-full text-left text-sm px-3 py-2 rounded-md transition-colors ${active === key ? "bg-crimson text-white" : "hover:bg-stone-100"}`} data-testid={`content-section-${key}`}>{label}</button>
          ))}
        </aside>
        <section className="bg-white border border-stone-200 rounded-lg p-6 space-y-6" data-testid={`content-panel-${active}`}>
          <h2 className="text-xl font-bold border-b border-stone-100 pb-3">{SECTIONS.find((s) => s[0] === active)?.[1]}</h2>
          {Object.keys(data[active] || {}).map((k) => (
            <Field key={`${active}-${k}`} section={active} k={k} value={data[active][k] ?? ""} onChange={set(k)} />
          ))}
        </section>
      </div>
    </div>
  );
}
